import type {
  ApiErrorBody,
  CatalogItem,
  CharacterDto,
  CharacterTypeDto,
  CreateCharacterInput,
  CropKey,
  FarmDto,
  FishingResultDto,
  FishingStartDto,
  GearSlot,
  HomeDto,
  InventoryItemDto,
  LayoutInput,
  LoginInput,
  MeDto,
  MineStatusDto,
  OreKey,
  PickaxeKey,
  ProgressDto,
  RegisterInput,
  ShopDto,
} from '@bb/shared';

import { noteFailedRequest } from './telemetry';

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public fields: Record<string, string> = {},
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

let csrfToken = '';

/** Time zone, language and screen size: part of the browser summary used for account-safety checks. */
const clientHint = (() => {
  try {
    return JSON.stringify({ tz: Intl.DateTimeFormat().resolvedOptions().timeZone, lang: navigator.language, screen: `${screen.width}x${screen.height}` });
  } catch {
    return '{}';
  }
})();

async function call<T>(method: string, url: string, body?: unknown, extra: Record<string, string> = {}): Promise<T> {
  let res: Response;
  try {
    res = await fetch(url, {
      method,
      credentials: 'same-origin',
      headers: {
        ...(body !== undefined ? { 'content-type': 'application/json' } : {}),
        ...(method !== 'GET' && csrfToken ? { 'x-csrf-token': csrfToken } : {}),
        'x-bb-client': clientHint,
        ...extra,
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch {
    noteFailedRequest(method, url, 0);
    throw new ApiError(0, 'network_error', "Can't reach Buddy Bay right now. Check your connection and try again.");
  }
  if (res.status === 204) return undefined as T;
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    const err = (data as ApiErrorBody | null)?.error;
    noteFailedRequest(method, url, res.status, err?.code);
    throw new ApiError(res.status, err?.code ?? 'error', err?.message ?? 'Something went wrong. Please try again.', err?.fields);
  }
  return data as T;
}

function remember(me: MeDto) {
  csrfToken = me.csrfToken;
  return me;
}

export const api = {
  me: () => call<MeDto>('GET', '/api/v1/me').then(remember),
  /** Players 16 and under get { pendingParent } instead of a session until a parent approves. */
  register: (input: RegisterInput) =>
    call<MeDto | { pendingParent: true; username: string } | { verifyEmail: true; username: string; email: string } | { underReview: true; username: string }>('POST', '/api/v1/auth/register', input).then((r) =>
      'pendingParent' in r || 'verifyEmail' in r || 'underReview' in r ? r : remember(r),
    ),
  /** With two-step on, the password step returns a ticket and `loginCode` finishes signing in. */
  login: (input: LoginInput) => call<MeDto | { twoStep: true; ticket: string }>('POST', '/api/v1/auth/login', input).then((r) => ('twoStep' in r ? r : remember(r))),
  loginCode: (ticket: string, code: string) => call<MeDto>('POST', '/api/v1/auth/login/two-step', { ticket, code }).then(remember),
  logout: () => call<void>('POST', '/api/v1/auth/logout'),
  characterTypes: () => call<{ types: CharacterTypeDto[] }>('GET', '/api/v1/catalog/character-types'),
  createCharacter: (input: CreateCharacterInput) =>
    call<{ character: CharacterDto }>('POST', '/api/v1/characters', input),
  catalog: () => call<{ items: CatalogItem[] }>('GET', '/api/v1/catalog/items'),
  wallet: () => call<{ wallet: Record<string, number>; progress: ProgressDto }>('GET', '/api/v1/wallet'),
  inventory: () => call<{ items: InventoryItemDto[] }>('GET', '/api/v1/inventory'),
  shop: (key: string) => call<ShopDto>('GET', `/api/v1/shops/${key}`),
  /** Each user action gets one key; retries of that action reuse it so it's charged once. */
  purchase: (listingId: number, key: string) =>
    call<{ balance: number; replayed: boolean }>('POST', '/api/v1/shops/purchase', { listingId }, { 'idempotency-key': key }),
  sell: (item: string, quantity: number, key: string) =>
    call<{ balance: number }>('POST', '/api/v1/inventory/sell', { item, quantity }, { 'idempotency-key': key }),
  equip: (slots: Record<string, string | null>) =>
    call<{ equipped: Record<string, string> }>('PUT', '/api/v1/characters/me/equipped', { slots }),
  startFishing: () => call<FishingStartDto>('POST', '/api/v1/minigames/fishing/sessions'),
  finishFishing: (id: string, outcome: 'caught' | 'escaped') =>
    call<FishingResultDto>('POST', `/api/v1/minigames/sessions/${id}/finish`, { outcome }),
  home: () => call<HomeDto>('GET', '/api/v1/homes/me'),
  saveHome: (layout: LayoutInput) => call<{ version: number }>('PUT', '/api/v1/homes/me/layout', layout),
};

export interface ProfileDto {
  displayName: string;
  characterType: string;
  species: string | null;
  appearance: Record<string, string>;
  equipped: Record<string, string>;
  level: number;
  memberSince: string | null;
  relation: 'self' | 'friend' | 'requested' | 'requested_you' | 'blocked' | 'none';
  badges: BadgeDto[];
  /** Verified, and 17 or over (younger players' verification isn't shown). */
  verified?: boolean;
}

export interface BadgeDto {
  key: string;
  name: string;
  description: string;
  icon: string;
  color: string;
}

/** Friends are accounts: `username` is the account, `displayName` the character it's playing as (if any). */
export interface SocialDto {
  friends: {
    username: string;
    displayName: string | null;
    characterType: string | null;
    species: string | null;
    appearance: Record<string, string>;
    equipped: Record<string, string>;
    online: boolean;
    area: string | null;
  }[];
  incoming: { id: number; username: string; displayName: string | null; viaInvite?: boolean }[];
  outgoing: { id: number; username: string; displayName: string | null; waitingForParent?: boolean }[];
  blocked: string[];
}

export interface AuthStatus {
  registrationOpen: boolean;
  inviteOnly: boolean;
  signupAges?: { minAge: number; closedBrackets: ('child' | 'teen' | 'adult')[] };
  banner?: { tone: 'info' | 'success' | 'warning'; message: string } | null;
}

export const social = {
  quick: () => call<{ phrases: { id: number; category: string; text: string }[]; freeChatEnabled: boolean }>('GET', '/api/v1/chat/quick'),
  overview: () => call<SocialDto>('GET', '/api/v1/social'),
  profile: (name: string) => call<ProfileDto>('GET', `/api/v1/players/${encodeURIComponent(name)}`),
  request: (displayName: string) => call<{ status: string; displayName: string }>('POST', '/api/v1/friends/requests', { displayName }),
  respond: (id: number, accept: boolean) => call('POST', `/api/v1/friends/requests/${id}`, { accept }),
  cancel: (id: number) => call('DELETE', `/api/v1/friends/requests/${id}`),
  unfriend: (name: string) => call('DELETE', `/api/v1/friends/${encodeURIComponent(name)}`),
  block: (displayName: string) => call('POST', '/api/v1/blocks', { displayName }),
  unblock: (name: string) => call('DELETE', `/api/v1/blocks/${encodeURIComponent(name)}`),
  report: (displayName: string, category: string, details?: string) => call('POST', '/api/v1/reports', { displayName, category, details }),
  visit: (name: string) =>
    call<{ owner: string; placements: { item: string; x: number; y: number; rotation: number; render: Record<string, unknown> }[] }>('GET', `/api/v1/homes/visit/${encodeURIComponent(name)}`),
  authStatus: () => call<AuthStatus>('GET', '/api/v1/auth/status'),
};

export interface ProgressNews {
  achievements: { key: string; name: string; reward: { coins?: number; xp?: number; candy?: number } }[];
  challenges: { key: string; name: string; reward: { coins?: number; xp?: number; candy?: number } }[];
  collections: { key: string; name: string; reward: { coins?: number; xp?: number; candy?: number } }[];
  quest?: { key: string; name: string; reward: { coins?: number; xp?: number; item?: string }; finished: boolean }[];
}

export interface ArcadeResult {
  score: number;
  won: boolean;
  coins: number;
  candy?: number;
  xp: number;
  flagged: boolean;
  dailyCapReached: boolean;
  reveal?: { correct?: number[]; right?: number };
  leveledUp: boolean;
  level?: number;
  progress?: ProgressNews;
}

export interface JournalDto {
  achievements: { key: string; name: string; description: string; icon: string; goal: number; progress: number; unlockedAt: string | null; reward: { coins?: number; xp?: number; candy?: number } }[];
  hiddenRemaining: number;
  collections: { key: string; name: string; description: string; found: number; total: number; reward: { coins?: number; xp?: number; candy?: number }; items: { key: string | null; name: string; rarity: string; found: boolean; type: string; slot: string | null; render: Record<string, unknown> }[] }[];
  challenges: { key: string; name: string; period: 'daily' | 'weekly' | 'event'; goal: number; progress: number; completed: boolean; reward: { coins?: number; xp?: number; candy?: number }; resets: string }[];
}

export interface SquadResult {
  won: boolean;
  coins: number;
  xp: number;
  score: number;
  seconds: number;
  flagged: boolean;
  dailyCapReached: boolean;
  leveledUp: boolean;
  level?: number;
  progress?: ProgressNews;
}
export interface RevealResponse {
  opened: { i: number; n: number }[];
  boom: boolean;
  won?: boolean;
  bombs?: number[];
  result?: SquadResult;
}
export type FarmResponse = { farm: FarmDto; progress?: ProgressNews };
export const farm = {
  get: () => call<FarmDto>('GET', '/api/v1/farm'),
  plant: (crop: CropKey, cells?: number[], allEmpty = false) => call<FarmResponse & { planted: number; spent: number }>('POST', '/api/v1/farm/plant', { crop, cells, allEmpty }),
  harvest: (cells?: number[], all = false) => call<FarmResponse & { total: number; xp: number; leveledUp: boolean; level: number }>('POST', '/api/v1/farm/harvest', { cells, all }),
  clear: (cells: number[]) => call<FarmResponse>('POST', '/api/v1/farm/clear', { cells }),
  upgrade: () => call<FarmResponse & { level: number; gift: string | null }>('POST', '/api/v1/farm/upgrade'),
  sell: (crop?: CropKey) => call<FarmResponse & { coins: number }>('POST', '/api/v1/farm/sell', crop ? { crop } : {}),
};

export const bombSquad = {
  start: (difficulty: 'rookie' | 'agent' | 'chief') =>
    call<{ sessionId: string; difficulty: string; w: number; h: number; bombs: number }>('POST', '/api/v1/games/bomb-squad', { difficulty }),
  reveal: (id: string, i: number) => call<RevealResponse>('POST', `/api/v1/games/bomb-squad/${id}/reveal`, { i }),
  sonar: (id: string, i: number) => call<{ scanned: { i: number; bomb: boolean }[] }>('POST', `/api/v1/games/bomb-squad/${id}/sonar`, { i }),
  invite: (friend: string) => call<{ roomId: string }>('POST', '/api/v1/games/bomb-squad/squad', { friend }),
};

export const games = {
  start: <T>(game: string) => call<T & { sessionId: string }>('POST', `/api/v1/arcade/${game}/sessions`),
  finish: (id: string, body: unknown) => call<ArcadeResult>('POST', `/api/v1/arcade/sessions/${id}/finish`, body),
  journal: () => call<JournalDto>('GET', '/api/v1/journal'),
  boards: () => call<{ boards: { key: string; name: string; unit: string }[] }>('GET', '/api/v1/leaderboards'),
  board: (key: string) => call<{ key: string; name: string; unit: string; rows: { rank: number; displayName: string; value: number }[] }>('GET', `/api/v1/leaderboards/${key}`),
};

export interface ModerationRecord {
  warnings: { id: number; rule: string; reason: string; status: string; createdAt: string }[];
  bans: { id: number; type: string; reason: string; startsAt: string; endsAt: string | null; liftedAt: string | null }[];
  appeals: { targetType: string; targetId: number; status: string; decisionNotes: string | null }[];
}

export interface SecurityDto {
  enabled: boolean;
  recoveryCodesLeft: number;
  email: string | null;
  /** When the email can next be changed (null = now). */
  nextEmailChange: string | null;
}
export interface VerificationDto {
  verified: boolean;
  verifiedAt: string | null;
  shownPublicly: boolean;
  request: { status: 'waiting_parent' | 'pending' | 'declined'; createdAt: string; note: string | null } | null;
  canApply: boolean;
  needsParent: boolean;
  twoStep: boolean;
  emailConfirmed: boolean;
}
export const verification = {
  mine: () => call<VerificationDto>('GET', '/api/v1/me/verification'),
  emailCode: () => call<{ sent: boolean; to: string }>('POST', '/api/v1/me/verification/email-code', {}),
  apply: (body: { email: string; emailCode: string; phone: string; birthDate: string; twoStepCode: string }) => call<{ status: string }>('POST', '/api/v1/me/verification', body),
  child: (childId: number) => call<{ verified: boolean; request: { status: string; createdAt: string } | null }>('GET', `/api/v1/family/children/${childId}/verification`),
  decideChild: (childId: number, decision: 'approve' | 'decline' | 'withdraw') => call('POST', `/api/v1/family/children/${childId}/verification`, { decision }),
};

export const account = {
  changePassword: (current: string, next: string) => call('POST', '/api/v1/me/password', { current, next }),
  security: () => call<SecurityDto>('GET', '/api/v1/me/security'),
  changeEmail: (newEmail: string, password: string, code?: string) => call<{ sent: boolean; to: string }>('POST', '/api/v1/me/email', { newEmail, password, ...(code ? { code } : {}) }),
  twoStepStart: () => call<{ secret: string; uri: string }>('POST', '/api/v1/me/two-step/start', {}),
  twoStepFinish: (code: string) => call<{ recoveryCodes: string[] }>('POST', '/api/v1/me/two-step/finish', { code }),
  twoStepOff: (password: string, code: string) => call('POST', '/api/v1/me/two-step/off', { password, code }),
  newRecoveryCodes: (password: string, code: string) => call<{ recoveryCodes: string[] }>('POST', '/api/v1/me/two-step/recovery-codes', { password, code }),
};

export interface ChildControls {
  chatMode: 'default' | 'quick_only';
  friendApproval: boolean;
  dailyLimitMinutes: number | null;
  allowedStart: number | null;
  allowedEnd: number | null;
  timezone: string;
  disabledGames: string[];
}
export type FamilyChild =
  | { id: number; username: string; displayName: string | null; ageBracket: string; agedOut: true }
  | {
      id: number;
      username: string;
      displayName: string | null;
      ageBracket: string;
      agedOut: false;
      quickChatOnly: boolean;
      email: string | null;
      controls: ChildControls;
      playedTodayMinutes: number;
      lockedNow: string | null;
      otherParents: string[];
      lastLoginAt: string | null;
    };
export interface FriendApproval {
  id: number;
  childId: number;
  childName: string | null;
  otherName: string;
  direction: 'outgoing' | 'incoming';
  createdAt: string;
}
export interface ConsentRequest {
  childUsername: string;
  ageBracket: string;
  quickChatOnly: boolean;
  parentEmail: string;
  expiresAt: string;
  alreadyApproved: boolean;
}

export interface CharacterSlot extends CharacterDto {
  slot: number;
  active: boolean;
}
export const characters = {
  list: () => call<{ characters: CharacterSlot[]; max: number }>('GET', '/api/v1/characters'),
  switchTo: (id: number) => call('POST', `/api/v1/characters/${id}/switch`),
};

export const family = {
  consent: (token: string) => call<ConsentRequest>('GET', `/api/v1/parent-consent?token=${encodeURIComponent(token)}`),
  approve: (token: string) => call<{ childUsername: string }>('POST', '/api/v1/parent-consent/approve', { token }),
  decline: (token: string) => call<{ deleted: boolean }>('POST', '/api/v1/parent-consent/decline', { token }),
  resendConsent: (login: string, password: string, parentEmail?: string) =>
    call<{ ok: boolean; alreadyApproved: boolean }>('POST', '/api/v1/auth/resend-parent-consent', { login, password, ...(parentEmail ? { parentEmail } : {}) }),
  overview: () => call<{ children: FamilyChild[]; approvals: FriendApproval[] }>('GET', '/api/v1/family'),
  saveControls: (childId: number, c: ChildControls) => call('PUT', `/api/v1/family/children/${childId}/controls`, c),
  decide: (requestId: number, approve: boolean) => call('POST', `/api/v1/family/friend-requests/${requestId}`, { approve }),
  chat: (childId: number, before?: number) =>
    call<{ messages: { id: number; channel: string; kind: string; content: string; status: string; createdAt: string; wasShown: boolean }[] }>(
      'GET',
      `/api/v1/family/children/${childId}/chat${before ? `?before=${before}` : ''}`,
    ),
  resetPassword: (childId: number, password: string) => call('POST', `/api/v1/family/children/${childId}/password`, { password }),
  invite: (childId: number, email: string) => call('POST', `/api/v1/family/children/${childId}/invite`, { email }),
  exportData: (childId: number) => call<Record<string, unknown>>('GET', `/api/v1/family/children/${childId}/export`),
  close: (childId: number, password: string) => call('POST', `/api/v1/family/children/${childId}/close`, { password }),
  changeEmail: (childId: number, newEmail: string, password: string, code?: string) =>
    call<{ sent: boolean; to: string }>('POST', `/api/v1/family/children/${childId}/email`, { newEmail, password, ...(code ? { code } : {}) }),
  activity: () => call<{ minutesLeft: number | null }>('POST', '/api/v1/me/activity'),
};

export const emailFlows = {
  verify: (token: string) => call<{ username: string }>('POST', '/api/v1/auth/verify-email', { token }),
  forgot: (email: string) => call<{ ok: boolean; message: string }>('POST', '/api/v1/auth/forgot-password', { email }),
  reset: (token: string, password: string) => call<{ username: string }>('POST', '/api/v1/auth/reset-password', { token, password }),
  resendSignedOut: (login: string, password: string) => call('POST', '/api/v1/auth/resend-verification', { login, password }),
  resend: () => call<{ sent: boolean }>('POST', '/api/v1/me/resend-verification'),
  confirmChange: (token: string) => call<{ username: string; email: string }>('POST', '/api/v1/account/email/confirm', { token }),
  undoChange: (token: string) => call<{ username: string; email: string }>('POST', '/api/v1/account/email/undo', { token }),
};

export const appeals = {
  mine: () => call<ModerationRecord>('GET', '/api/v1/me/moderation'),
  file: (targetType: 'warning' | 'ban', targetId: number, reason: string) => call('POST', '/api/v1/appeals', { targetType, targetId, reason }),
  lookup: (login: string, password: string) => call<ModerationRecord>('POST', '/api/v1/appeals/lookup', { login, password }),
  fileSignedOut: (login: string, password: string, targetType: 'warning' | 'ban', targetId: number, reason: string) =>
    call('POST', '/api/v1/appeals/signed-out', { login, password, targetType, targetId, reason }),
};

export interface EventDto {
  key: string;
  name: string;
  description: string;
  startsAt: string;
  endsAt: string;
  live: boolean;
  theme: { decor?: string };
  shopKey: string | null;
}

export const events = { list: () => call<{ events: EventDto[] }>('GET', '/api/v1/events') };

export interface PolicyDto {
  slug: string;
  title: string;
  body: string;
  updatedAt: string;
  version: number;
}

export const policies = {
  list: () => call<{ pages: { slug: string; title: string; updatedAt: string }[] }>('GET', '/api/v1/policies'),
  get: (slug: string) => call<PolicyDto>('GET', `/api/v1/policies/${encodeURIComponent(slug)}`),
};

export interface BetaPhase {
  name: string;
  status: 'upcoming' | 'open' | 'ended';
  startDate: string;
  endDate: string;
  description: string;
}
export type BetaPageDto =
  | { enabled: false; title: string; disabledMessage: string }
  | { enabled: true; title: string; tagline: string; intro: string; eligibility: string; howToApply: string; applyLabel: string; applyUrl: string; phases: BetaPhase[]; more: string };

export interface FormFieldDto {
  id: string;
  type: 'short_text' | 'long_text' | 'email' | 'number' | 'date' | 'single_choice' | 'multi_choice' | 'dropdown' | 'agree' | 'section';
  label: string;
  help: string;
  required: boolean;
  options: string[];
  maxLength?: number;
}
export interface FormDto {
  slug: string;
  title: string;
  description: string;
  access: 'public' | 'restricted' | 'locked';
  status: 'draft' | 'open' | 'closed';
  fields: FormFieldDto[];
  signedIn: boolean;
  closedReason: string | null;
}

export const site = {
  betaPage: () => call<BetaPageDto>('GET', '/api/v1/beta-page'),
  form: (slug: string) => call<FormDto>('GET', `/api/v1/forms/${encodeURIComponent(slug)}`),
  submitForm: (slug: string, answers: Record<string, unknown>, website: string) =>
    call<{ id: number; confirmation: string }>('POST', `/api/v1/forms/${encodeURIComponent(slug)}/responses`, { answers, ...(website ? { website } : {}) }),
};

export interface MineEventDto {
  kind: 'ore' | 'artifact' | 'key' | 'chest' | 'gas' | 'rockfall' | 'full' | 'floor' | 'level' | 'badge' | 'fainted' | 'worn' | 'broken';
  text: string;
}
export interface MineEnded {
  kept: Partial<Record<string, number>>;
  coins: number;
  accountXp: number;
  leveledUp: boolean;
  deepestFloor: number;
  fainted: boolean;
}
export type MineResponse = { status: MineStatusDto; events: MineEventDto[]; ended?: MineEnded; progress?: ProgressNews };
export const mine = {
  get: () => call<MineStatusDto>('GET', '/api/v1/mine'),
  dive: (camp: number) => call<MineResponse>('POST', '/api/v1/mine/dive', { camp }),
  dig: (i: number) => call<MineResponse>('POST', '/api/v1/mine/dig', { i }),
  descend: () => call<MineResponse>('POST', '/api/v1/mine/descend', {}),
  climbOut: () => call<MineResponse>('POST', '/api/v1/mine/climb-out', {}),
  craft: (body: { kind: 'pickaxe'; pickaxe: PickaxeKey } | { kind: GearSlot }) => call<{ made: string; balance: number; status: MineStatusDto; progress?: ProgressNews }>('POST', '/api/v1/mine/craft', body),
  repair: () => call<{ repaired: string; balance: number; status: MineStatusDto }>('POST', '/api/v1/mine/repair', {}),
  sell: (ore?: OreKey) => call<{ sold: Partial<Record<OreKey, number>>; coins: number; status: MineStatusDto; progress?: ProgressNews }>('POST', '/api/v1/mine/sell', ore ? { ore } : {}),
};

/** A player's badges and how many may show; `parentChooses` for players aged 13 and under. */
export interface BadgeListDto {
  badges: (BadgeDto & { featured: boolean; earnedAt: string })[];
  limit: number;
  parentChooses: boolean;
}
export const badges = {
  mine: () => call<BadgeListDto>('GET', '/api/v1/badges'),
  feature: (keys: string[]) => call<BadgeListDto>('PUT', '/api/v1/badges/featured', { keys }),
  child: (childId: number) => call<BadgeListDto>('GET', `/api/v1/family/children/${childId}/badges`),
  featureForChild: (childId: number, keys: string[]) => call<BadgeListDto>('PUT', `/api/v1/family/children/${childId}/badges`, { keys }),
};

export interface PlayerInviteDto {
  code: string;
  uses: number;
  maxUses: number;
  expiresAt: string;
  createdAt: string;
  status: 'open' | 'used_up' | 'expired' | 'cancelled';
  /** Whoever joins with it sends you a friend request. */
  friendRequest: boolean;
}
export const invites = {
  mine: () =>
    call<{ codes: PlayerInviteDto[]; blocked: string | null; usesPerCode: number; days: number; madeRecently: number; limit: number; windowDays: number; signupClosed: boolean }>('GET', '/api/v1/invites'),
  create: (friendRequest: boolean) => call<{ code: string; expiresAt: string; maxUses: number }>('POST', '/api/v1/invites', { friendRequest }),
  setFriendRequest: (code: string, friendRequest: boolean) => call('PATCH', `/api/v1/invites/${encodeURIComponent(code)}`, { friendRequest }),
  cancel: (code: string) => call('DELETE', `/api/v1/invites/${encodeURIComponent(code)}`),
};

export interface ConversationDto {
  id: number;
  kind: 'direct' | 'group';
  name: string | null;
  myStatus: 'active' | 'invited' | 'awaiting_parent';
  isOwner: boolean;
  members: { userId: number; username: string; displayName: string | null; status: string }[];
  unread: number;
  last: { text: string; at: string } | null;
  monitored: boolean;
  ageRule: string;
}
export interface PrivateMessageDto {
  id: number;
  from: string;
  fromId: number | null;
  text: string;
  status: 'shown' | 'filtered' | 'blocked' | 'removed';
  at: string;
}
export interface MessagingSettingsDto {
  inviteOnly: boolean;
  ageOptOut: boolean;
  canOptOut: boolean;
  canMessage: boolean;
  verified: boolean;
  age: number | null;
}
export const messages = {
  list: () => call<{ conversations: ConversationDto[]; settings: MessagingSettingsDto }>('GET', '/api/v1/messages'),
  unread: () => call<{ unread: number; invites: number }>('GET', '/api/v1/messages/unread'),
  read: (id: number) => call<{ messages: PrivateMessageDto[]; monitored: boolean }>('GET', `/api/v1/messages/${id}`),
  send: (id: number, text: string) => call<{ id: number; status: string; notice?: string }>('POST', `/api/v1/messages/${id}`, { text }),
  direct: (name: string) => call<{ id: number }>('POST', '/api/v1/messages/direct', { name }),
  createGroup: (name: string, members: string[]) => call<{ id: number; added: { name: string; status: string; reason?: string }[] }>('POST', '/api/v1/messages/groups', { name, members }),
  addMember: (id: number, name: string) => call<{ name: string; status: string }>('POST', `/api/v1/messages/${id}/members`, { name }),
  removeMember: (id: number, userId: number) => call('DELETE', `/api/v1/messages/${id}/members/${userId}`),
  rename: (id: number, name: string) => call('PATCH', `/api/v1/messages/${id}`, { name }),
  answerInvite: (id: number, accept: boolean) => call('POST', `/api/v1/messages/${id}/invite`, { accept }),
  leave: (id: number) => call('POST', `/api/v1/messages/${id}/leave`, {}),
  report: (messageId: number, category: string, details?: string) => call('POST', '/api/v1/messages/report', { messageId, category, ...(details ? { details } : {}) }),
  setInviteOnly: (inviteOnly: boolean) => call('PUT', '/api/v1/messages/settings', { inviteOnly }),
  setAgeOptOut: (optOut: boolean, emailCode?: string) => call('POST', '/api/v1/messages/age-opt-out', { optOut, ...(emailCode ? { emailCode } : {}) }),
};

export interface ChildMessagingDto {
  canMessage: boolean;
  age: number | null;
  friends: { userId: number; username: string; age: number | null; sameGroup: boolean; adult: boolean; approved: boolean }[];
  waiting: { conversationId: number; groupName: string | null; userId: number; username: string; age: number | null; reason: string }[];
  ownedGroups: { id: number; name: string | null; ageRule: string }[];
  conversations: ConversationDto[];
}
export const childMessages = {
  overview: (childId: number) => call<ChildMessagingDto>('GET', `/api/v1/family/children/${childId}/messages`),
  setContact: (childId: number, otherId: number, allow: boolean, confirmAge = false) => call('POST', `/api/v1/family/children/${childId}/messages/contacts/${otherId}`, { allow, confirmAge }),
  decideMember: (childId: number, conversationId: number, memberId: number, approve: boolean) =>
    call('POST', `/api/v1/family/children/${childId}/messages/groups/${conversationId}/members/${memberId}`, { approve }),
  setAgeRule: (childId: number, conversationId: number, open: boolean) => call('POST', `/api/v1/family/children/${childId}/messages/groups/${conversationId}/age-rule`, { open }),
  read: (childId: number, conversationId: number) => call<{ messages: PrivateMessageDto[] }>('GET', `/api/v1/family/children/${childId}/messages/${conversationId}`),
};

export const newKey = () => crypto.randomUUID().replace(/-/g, '');

export interface BugReportsDto {
  categories: { key: string; label: string }[];
  mine: { id: number; category: string; description: string; status: string; createdAt: string }[];
}

export const feedback = {
  list: () => call<BugReportsDto>('GET', '/api/v1/bug-reports'),
  send: (body: {
    category: string;
    description: string;
    area?: string;
    clientVersion?: string;
    context: Record<string, unknown>;
    screenshot?: string;
  }) => call<{ id: number }>('POST', '/api/v1/bug-reports', body),
};

export interface StarterQuestDto {
  /** Off while staff have switched the guide off. */
  enabled: boolean;
  title: string;
  steps: { key: string; name: string; hint: string; coins: number; xp: number; done: boolean }[];
  finished: boolean;
  hidden: boolean;
  skipped: boolean;
  stepReward: { coins: number; xp: number };
  finishReward: { coins: number; xp: number; item: string | null };
}

export interface DailyRewardDto {
  claimedToday: boolean;
  resetsAt: string;
  totalClaims: number;
  days: { day: number; coins: number; xp: number; item: string | null; state: 'claimed' | 'today' | 'upcoming' }[];
}

export interface GiftItemDto {
  key: string;
  name: string;
  rarity: string;
  type: string;
  render: Record<string, unknown>;
}
export interface GiftDto {
  id: number;
  from: string;
  to: string;
  direction: 'sent' | 'received';
  item: GiftItemDto;
  quantity: number;
  note: string | null;
  createdAt: string;
  openedAt: string | null;
}
export interface GiftBoxDto {
  enabled: boolean;
  waiting: GiftDto[];
  history: GiftDto[];
  sentToday: number;
  dailyLimit: number;
  notes: { key: string; text: string }[];
}

export const progression = {
  quest: () => call<StarterQuestDto>('GET', '/api/v1/starter-quest'),
  setQuestHidden: (hidden: boolean) => call<StarterQuestDto>('POST', '/api/v1/starter-quest/hidden', { hidden }),
  setQuestSkipped: (skipped: boolean) => call<StarterQuestDto>('POST', '/api/v1/starter-quest/skipped', { skipped }),
  daily: () => call<DailyRewardDto>('GET', '/api/v1/daily-reward'),
  claimDaily: () =>
    call<{ day: number; coins: number; xp: number; item: string | null; leveledUp: boolean; progress?: ProgressNews; calendar: DailyRewardDto }>('POST', '/api/v1/daily-reward/claim'),
};

export const gifts = {
  box: () => call<GiftBoxDto>('GET', '/api/v1/gifts'),
  giftable: () => call<{ items: (GiftItemDto & { available: number })[] }>('GET', '/api/v1/gifts/giftable'),
  send: (body: { to: string; item: string; quantity: number; note?: string }) =>
    call<{ id: number; to: string; item: string; quantity: number; sentToday: number; dailyLimit: number; progress?: ProgressNews }>('POST', '/api/v1/gifts', body),
  open: (id: number) => call<{ item: string; quantity: number; from: string; progress?: ProgressNews }>('POST', `/api/v1/gifts/${id}/open`),
};
